// hooks/useGeolocation.ts
import { useState, useCallback, useRef, useMemo } from 'react';
import { fetchCityName, createGoogleApiError } from '../../util/google-api/google-api.util';
import { reportError } from '../bugsnag';

interface GeolocationState {
  loading: boolean;
  error: string | null;
  address: string | null;
  formattedAddress: string | null;
  coordinates: {
    latitude: number | null;
    longitude: number | null;
  };
}

interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  removePrefix?: boolean;
}

interface GeolocationHookResult extends GeolocationState {
  getCurrentPlace: (e?: React.MouseEvent<HTMLAnchorElement>) => Promise<GeolocationResult>;
}

interface GeolocationResult {
  formattedAddress: string;
  fullAddress: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

/**
 * 位置情報を取得するためのカスタムフック。
 * ブラウザのGeolocation APIを使用して現在地を取得し、Google Maps Geocoding APIを使用して住所に変換します。
 *
 * @param {GeolocationOptions} options - 位置情報取得の設定オプション
 * @param {boolean} options.enableHighAccuracy - 高精度の位置情報を取得するかどうか (デフォルト: true)
 * @param {number} options.timeout - タイムアウトの時間（ミリ秒） (デフォルト: 60000)
 * @param {number} options.maximumAge - キャッシュされた位置情報の最大有効期間（ミリ秒） (デフォルト: 3000)
 * @param {boolean} options.removePrefix - 住所から「日本、〒XXX-XXXX」の部分を削除するかどうか (デフォルト: true)
 *
 * @returns {GeolocationHookResult} 位置情報の状態と操作メソッドを含むオブジェクト
 * @returns {boolean} returns.loading - 位置情報を取得中かどうか
 * @returns {string|null} returns.error - エラーメッセージ（エラーがない場合はnull）
 * @returns {string|null} returns.address - 取得した完全な住所（取得前はnull）
 * @returns {string|null} returns.formattedAddress - 整形された住所（prefix削除済み、取得前はnull）
 * @returns {Object} returns.coordinates - 緯度・経度の情報
 * @returns {number|null} returns.coordinates.latitude - 緯度（取得前はnull）
 * @returns {number|null} returns.coordinates.longitude - 経度（取得前はnull）
 * @returns {Function} returns.getCurrentPlace - 現在地を取得する関数。
 *   引数にReactのクリックイベント（e）を渡すことができ、その場合はe.preventDefault()が呼ばれます。
 *   成功時は{formattedAddress, fullAddress, coordinates}を含むPromiseを返します。
 *   失敗時はエラーをrejectするPromiseを返します。
 */

export const useGeolocation = (options?: GeolocationOptions): GeolocationHookResult => {
  const [state, setState] = useState<GeolocationState>({
    loading: false,
    error: null,
    address: null,
    formattedAddress: null,
    coordinates: {
      latitude: null,
      longitude: null
    }
  });

  const lastRequestId = useRef<number>(0);

  const defaultOptions = useMemo(
    () => ({
      enableHighAccuracy: true,
      timeout: 60000,
      maximumAge: 3000,
      removePrefix: true,
      ...options
    }),
    [options]
  );

  const getCurrentPlace = useCallback(
    (e?: React.MouseEvent<HTMLAnchorElement>) => {
      if (e) {
        e.preventDefault();
      }

      if (!navigator.geolocation) {
        const error = createGoogleApiError('位置情報取得に対応していません', 'GEOLOCATION_NOT_SUPPORTED');
        reportError(error);
        setState(prev => ({
          ...prev,
          loading: false,
          error: '位置情報取得に対応していません'
        }));
        return Promise.reject(error);
      }

      setState(prev => ({ ...prev, loading: true, error: null }));

      lastRequestId.current += 1;
      const currentRequestId = lastRequestId.current;

      return new Promise<{
        formattedAddress: string;
        fullAddress: string;
        coordinates: { latitude: number; longitude: number };
      }>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          position => {
            if (currentRequestId !== lastRequestId.current) {
              return;
            }

            const { latitude, longitude } = position.coords;

            fetchCityName(latitude, longitude)
              .then(result => {
                if (currentRequestId !== lastRequestId.current) {
                  return;
                }

                if (result.status === 'OK' && result.results.length > 0) {
                  const fullAddress = result.results[0].formatted_address;
                  const formattedAddress = defaultOptions.removePrefix
                    ? fullAddress.replace(/^日本、〒\d{3}-\d{4} /g, '')
                    : fullAddress;

                  setState({
                    loading: false,
                    error: null,
                    address: fullAddress,
                    formattedAddress,
                    coordinates: { latitude, longitude }
                  });

                  resolve({
                    formattedAddress,
                    fullAddress,
                    coordinates: { latitude, longitude }
                  });
                } else {
                  const errorMsg = '位置情報の取得に失敗しました。';
                  const error = createGoogleApiError(errorMsg, result.status);
                  reportError(error);
                  setState(prev => ({
                    ...prev,
                    loading: false,
                    error: errorMsg
                  }));
                  reject(error);
                }
              })
              .catch(err => {
                if (currentRequestId !== lastRequestId.current) {
                  return;
                }
                const errorMsg = '位置情報の取得に失敗しました。';
                reportError(err);
                setState(prev => ({
                  ...prev,
                  loading: false,
                  error: errorMsg
                }));
                reject(err);
              });
          },
          err => {
            if (currentRequestId !== lastRequestId.current) {
              return;
            }

            let errorMsg = '位置情報の取得に失敗しました。';

            switch (err.code) {
              case 1:
                errorMsg = '位置情報取得権限がありません。';
                break;
              case 2:
                errorMsg = '位置情報が特定できませんでした。';
                break;
              case 3:
                errorMsg = '位置情報取得がタイムアウトしました。';
                break;
              default:
                errorMsg = 'その他のエラー。位置情報の取得に失敗しました。';
            }

            const error = createGoogleApiError(errorMsg, `GEOLOCATION_ERROR_${err.code}`);
            reportError(error);

            setState(prev => ({
              ...prev,
              loading: false,
              error: errorMsg
            }));

            reject(error);
          },
          defaultOptions
        );
      });
    },
    [defaultOptions]
  );

  return {
    ...state,
    getCurrentPlace
  };
};
